import { useAtomValue } from 'jotai';
import { useMemo } from 'react';
import { DataTableSkeleton } from '@carbon/react';
import { TractNriMatrixTable } from './tract-nri-matrix-table';
import styles from './tract-nri-matrix-popup.module.scss';
import { tractNriMatrixAtomFamily } from 'atoms/tract-nri-matrix-atoms';
import type { TractNriMatrixResponse } from 'types/api-responses';
import { CalculationType } from 'components/nri-table/types';
import { EmptyState } from 'components/empty-state';
import NoDataSvg from 'images/srp-illustration-data.svg';

interface TractNriMatrixPopupProps {
  tractId: string;
  /** For LTD slots only */
  slotId?: string;
  /** The well UWI - used for non-LTD slots */
  wellUwi?: string;
  /** On a package page: only the package's own Subtracts of the Tract. */
  packageId?: number;
  calculationType?: CalculationType;
}

export const TractNriMatrixPopup = ({
  tractId,
  slotId,
  wellUwi,
  packageId,
  calculationType = 'NRI',
}: TractNriMatrixPopupProps) => {
  // Create stable key for atomFamily - each unique combination gets its own atom instance
  const matrixKey = useMemo(
    () => ({ tractId, slotId, wellUwi, packageId }),
    [tractId, slotId, wellUwi, packageId]
  );

  // Use atomFamily directly - no need for params atom or useEffect
  const tractNriMatrixLoadable = useAtomValue(
    tractNriMatrixAtomFamily(matrixKey)
  );

  const matrixData: TractNriMatrixResponse | null = useMemo(() => {
    if (
      tractNriMatrixLoadable.state === 'hasData' &&
      tractNriMatrixLoadable.data
    ) {
      return tractNriMatrixLoadable.data;
    }
    return null;
  }, [tractNriMatrixLoadable]);

  const isLoading = tractNriMatrixLoadable.state === 'loading';
  const hasError = tractNriMatrixLoadable.state === 'hasError';

  const slotName = matrixData
    ? (matrixData.wellName ?? matrixData.uwi ?? matrixData.slotId ?? '--')
    : '--';

  const titlePrefix =
    calculationType === 'WI' ? 'Total Slot WI Matrix' : 'Total Slot NRI Matrix';

  if (isLoading) {
    return (
      <div data-portal-container>
        <div className={styles.title}>
          {titlePrefix}
          <span className={styles.subtitle}>({slotName})</span>
        </div>
        <div className={styles.tableContainer}>
          <DataTableSkeleton
            columnCount={3}
            rowCount={3}
            showHeader={false}
            showToolbar={false}
          />
        </div>
      </div>
    );
  }

  if (hasError) {
    return (
      <div data-portal-container>
        <div className={styles.title}>
          {titlePrefix}
          <span className={styles.subtitle}>({slotName})</span>
        </div>
        <div className={styles.tableContainer}>
          Error loading {calculationType} data
        </div>
      </div>
    );
  }

  if (!matrixData) {
    return (
      <div data-portal-container>
        <div className={styles.title}>
          {titlePrefix}
          <span className={styles.subtitle}>({slotName})</span>
        </div>
        <div className={styles.tableContainer}>
          <EmptyState
            icon={NoDataSvg}
            headerText={`No ${calculationType} data`}
            helperText={`No ${calculationType} data is available for this slot.`}
          />
        </div>
      </div>
    );
  }

  // Ensure rows and breakdown are valid arrays before rendering
  const rows = matrixData.rows ?? [];
  const breakdown = matrixData.breakdown ?? [];

  return (
    <div data-portal-container>
      <div className={styles.title}>
        {titlePrefix}
        <span className={styles.subtitle}>({slotName})</span>
      </div>
      <div className={styles.tableContainer}>
        <TractNriMatrixTable
          rows={rows}
          breakdown={breakdown}
          calculationType={calculationType}
          wellName={matrixData.wellName ?? undefined}
        />
      </div>
    </div>
  );
};
